import { motion } from "framer-motion";
import { PILLARS, MISSION } from "@/data/content";
import CTASection from "@/components/sections/CTASection";

/* Gold-stroke pillar icons - match the ServicesGrid set. */
const ICONS = {
  builder: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 22 L12 16 L16 20 L26 10" />
      <path d="M6 10 L26 10 L26 24 L6 24 Z" />
      <circle cx="10" cy="14" r="1.2" fill="currentColor" />
    </svg>
  ),
  brand: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 10 L 14 10 L 10 14 L 16 20 L 12 24" />
      <circle cx="22" cy="10" r="2.5" />
    </svg>
  ),
  content: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8 L 22 8 L 26 12 L 26 24 L 6 24 Z" />
      <path d="M22 8 L 22 12 L 26 12" />
      <path d="M10 16 L 22 16" />
      <path d="M10 19 L 18 19" />
    </svg>
  ),
  growth: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 24 L 12 17 L 17 21 L 27 9" />
      <path d="M21 9 L 27 9 L 27 15" />
      <circle cx="12" cy="17" r="1.4" fill="currentColor" />
      <circle cx="17" cy="21" r="1.4" fill="currentColor" />
    </svg>
  ),
  vault: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="4.5" />
      <path d="M14.2 14.2 L 24 24" />
      <path d="M20 22 L 22 24 L 24 22" />
    </svg>
  ),
  insights: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 22 L 11 22 L 11 14 L 11 22 L 17 22 L 17 9 L 17 22 L 23 22 L 23 17 L 23 22 L 29 22" />
    </svg>
  ),
};

export default function PlatformPage() {
  return (
    <div data-testid="platform-page" className="pt-32 sm:pt-40">
      <section className="px-5 sm:px-6 lg:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1400px] mx-auto">
          <p className="overline">The platform</p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[9rem] leading-[0.92] tracking-tight mt-5 sm:mt-6 max-w-[14ch]">
            One <span className="italic text-aurora">studio,</span>
            <br />
            end to end.
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-700 max-w-2xl leading-relaxed mt-8 sm:mt-10">
            {MISSION}
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-6 lg:px-12 py-12 sm:py-20 border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto space-y-16 sm:space-y-24">
          {PILLARS.map((p, i) => {
            const flip = i % 2 === 1;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                data-testid={`platform-pillar-${p.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
              >
                <div className={`lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`}>
                  <p className="overline">{p.eyebrow}</p>
                  <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05] mt-4 sm:mt-6">
                    {p.title}
                  </h2>
                  <p className="text-neutral-700 mt-5 sm:mt-6 leading-relaxed text-base sm:text-lg">
                    {p.short}
                  </p>
                </div>
                <div className={`lg:col-span-6 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
                  <div className="border border-neutral-200 hover:border-gold/40 transition-colors p-6 sm:p-10 bg-white relative overflow-hidden">
                    <div className="text-gold mb-6 sm:mb-8">
                      {ICONS[p.id]}
                    </div>
                    <ul className="space-y-3 sm:space-y-4">
                      {p.bullets.map((b) => (
                        <li
                          key={b}
                          className="text-neutral-600 text-sm sm:text-base flex gap-3 items-start leading-relaxed"
                        >
                          <span className="text-gold mt-[10px] block w-3 h-px bg-gold flex-none" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
